import React from 'react'
import * as joda from '@js-joda/core'
import { usePrevious } from 'primereact/hooks'
import { CountdownCircleTimer } from 'react-countdown-circle-timer'
import { _postV1 } from '_services/config'
import { requestMediaUrl } from '_services/users.service'
import { uploadFileToPresignedUrl } from '_services/fetches/S3upload'
import { UserVideoInterview } from 'types/user'
import MediaDeviceSelection,{ MediaDeviceOptions,MediaDevicesSelected } from '../../../../../../components/media-device-selection'
import Await from '../../../../../../_core/components/await'
import TimeEventEmitter from '../../../../../../_core/components/time-event-emitter'
import useMediaDevices,{ MediaDeviceInfoMediaDeviceSelection } from '../../../../../../_core/hooks/useMediaDevices'
import { State,Dispatch,dispatcher,stateToR,makeReducer } from 'util/state-machine'
import KnobCounter from '../../../../../../components/knob-counter'

type Question = {
  id:string
  question:string
}
type PresignedUrl = {
  id      :string
  userId  :string
  url     :string
  location:string
  mimeType:string
}
enum Stage {
  ERROR             ='error',
  // INITIAL           ='initial',
  PERMISSIONS       ='permissions',
  PERMISSIONS_FAILED='permissions-failed',
  DEVICES           ='devices',
  ROOT              ='root',
  QUESTION_LOADING  ='question-loading',
  QUESTION          ='question',
  RECORDING         ='recording',
  REVIEWING         ='reviewing',
  REVIEWING_ERROR   ='reviewing-error',
  UPLOADING         ='uploading',
}
enum Event {
  ERROR='error',
  PERMISSIONS_CHECK='permissions-check',
  PERMISSIONS_FAILED='permissions-failed',
  GOTO_ROOT='goto-root',
  SET_DEVICES='set-devices',
  QUESTION_LOADING='question-loading',
  QUESTION_LOADED='question-loaded',
  RECORDING_START='recording-start',
  RECORDING_MEETS='recording-meets',
  REVIEW='review',
  REVIEW_ERROR='review-error',
  UPLOAD='upload',
}
type Events = {
  [Event.ERROR]            :{}
  [Event.PERMISSIONS_CHECK]:Promise<void>
  [Event.PERMISSIONS_FAILED]:{}
  [Event.SET_DEVICES]      :{ devices:MediaDeviceOptions,selected:MediaDevicesSelected | undefined,wait:Promise<void> }
  [Event.GOTO_ROOT]        :{ devices:MediaDeviceOptions,selected:MediaDevicesSelected | undefined,stream:MediaStream }
  [Event.QUESTION_LOADED]  :{ question:Question }
  [Event.QUESTION_LOADING] :{ question:Promise<void>}
  [Event.RECORDING_START]  :{ recorder:MediaRecorder }
  [Event.RECORDING_MEETS]  :{}
  [Event.REVIEW]           :{ question:Question,blob:Blob,objectUrl:string }
  [Event.REVIEW_ERROR]     :{ 'message':string }
  [Event.UPLOAD]           :Promise<void>
}

type States = {
  [Stage.ERROR]             :string | undefined
  [Stage.PERMISSIONS]       :Promise<void>
  [Stage.PERMISSIONS_FAILED]:undefined
  [Stage.DEVICES]           :{ devices:MediaDeviceOptions,selected:MediaDevicesSelected | undefined,wait:Promise<void> }
  [Stage.ROOT]:{
    stream:MediaStream
    devices:MediaDeviceOptions
    selected:MediaDevicesSelected | undefined
    preview:React.ReactNode
  }
  [Stage.QUESTION_LOADING]:{
    stream:MediaStream
    selected:MediaDevicesSelected | undefined
    preview:React.ReactNode
    question:Promise<void>
  }
  [Stage.QUESTION]:{
    stream:MediaStream
    selected:MediaDevicesSelected | undefined
    preview:React.ReactNode
    question:Question
    epoch:joda.Instant
  }
  [Stage.RECORDING]:{
    meets:boolean
    recorder:MediaRecorder
    stream:MediaStream
    selected:MediaDevicesSelected | undefined
    preview:React.ReactNode
    question:Question
    epoch:joda.Instant
  }
  [Stage.REVIEWING]:{
    selected:MediaDevicesSelected | undefined
    question:Question
    blob:Blob
    objectUrl:string
  }
  [Stage.REVIEWING_ERROR]:{
    selected:MediaDevicesSelected | undefined
    message:string
  }
  [Stage.UPLOADING]:{
    upload:Promise<void>
  }
}

type Props = {
  replace?:UserVideoInterview | null
  onUpload:(_:string) => void
}

const constQuestionTime : joda.Duration = joda.Duration.ofSeconds(5)
// const constQuestionTime : joda.Duration = joda.Duration.ZERO
const constMinLength : joda.Duration = joda.Duration.ofSeconds(15)
const constMaxLength : joda.Duration = joda.Duration.ofSeconds(75)
const constMaxRecentQuestions = 5
const constVideoWidth = 221
const constVideoHeight = 296
const constVideoAspectRatio = constVideoHeight/constVideoWidth

const formatDurationText = (x:joda.Duration) : string => {
  const seconds = x.get(joda.ChronoUnit.SECONDS)
  const pieces = [] as string[]
  if(seconds > 60)
  {
    const minutes = Math.floor(seconds / 60)
    pieces.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`)
  }
  pieces.push(`${seconds % 60} seconds`)
  return pieces.join(' and ')
}
const recorderSettings : () => MediaRecorderOptions & { 'mimeType':string } = () => {
  if(MediaRecorder.isTypeSupported('video/webm; codecs=vp9'))
    return { 'mimeType':'video/webm; codecs=vp9' }
  else if(MediaRecorder.isTypeSupported('video/webm'))
    return { 'mimeType':'video/webm' }
  else if(MediaRecorder.isTypeSupported('video/mp4'))
    return { 'mimeType':'video/mp4','videoBitsPerSecond':100000 }
  else
    throw 'Neither webm nor mp4 supported!'
}
const getQuestion = async (recentQuestions:Question[]) : Promise<Question> => (await _postV1('/video-interviews/get-question',{
  'recentQuestions':recentQuestions.map((x) => x.id),
})).data
const getUploadUrl = async () : Promise<PresignedUrl> => requestMediaUrl(recorderSettings().mimeType)

/*
const doDownload = (objectUrl:string,filename:string) => {
  const a = document.createElement('a')
  document.body.appendChild(a)
  a.style.display = 'none'
  a.href = objectUrl
  a.download = `${filename}.webm`
  a.click()
}
*/

let mutableChunks = [] as Blob[]
let mutableRecentQuestions = [] as Question[]

const destroyMediaStream = (x:MediaStream | null) => {
  x?.getTracks().forEach((x) => x.stop())
}

const reducer = makeReducer<States,Events>((_1,_2,t) => [
  t(Event.PERMISSIONS_CHECK,undefined,Stage.PERMISSIONS,(a) => a),
  t(Event.PERMISSIONS_FAILED,Stage.PERMISSIONS,Stage.PERMISSIONS_FAILED,(_) => undefined),
  t(Event.PERMISSIONS_FAILED,Stage.PERMISSIONS_FAILED,Stage.PERMISSIONS_FAILED,(_) => undefined),
  t(Event.ERROR,undefined,Stage.ERROR,(a) => undefined),
  t(Event.SET_DEVICES,Stage.PERMISSIONS,Stage.DEVICES,(a,s) => a),
  t(Event.SET_DEVICES,Stage.ROOT,Stage.DEVICES,(a,s) => a),
  t(Event.SET_DEVICES,Stage.DEVICES,Stage.DEVICES,(a,s) => a),
  t(Event.GOTO_ROOT,Stage.ROOT,Stage.ROOT,(_,s) => s),
  t(Event.GOTO_ROOT,Stage.DEVICES,Stage.ROOT,(a,s) => {
    return {
      'stream':a.stream,
      'devices':a.devices,
      'selected':a.selected,
      'video':null,
      'preview':<>
        <div className="position-relative" key="preview">
          <video
            ref={(x) => {
              if(x)
              {
                x.srcObject = a.stream
              }
            }}
            autoPlay
            playsInline
            muted
            width={constVideoWidth+'px'}
            height={constVideoWidth*constVideoAspectRatio+'px'}
            className="rounded-2 z-0 nonsense"
          ></video>
        </div>
      </>
    }
  }),
  t(Event.QUESTION_LOADING,Stage.ROOT,Stage.QUESTION_LOADING,(a,s) => ({
    'stream':s.stream,
    'selected':s.selected,
    'preview':s.preview,
    'question':a.question,
  })),
  t(Event.QUESTION_LOADED,Stage.QUESTION_LOADING,Stage.QUESTION,(a,s) => ({
    'stream':s.stream,
    'selected':s.selected,
    'preview':s.preview,
    'question':a.question,
    'epoch':joda.Instant.now(),
  })),
  t(Event.RECORDING_START,Stage.QUESTION,Stage.RECORDING,(a,s) => ({
    'meets':false,
    'recorder':a.recorder,
    'stream':s.stream,
    'selected':s.selected,
    'preview':s.preview,
    'question':s.question,
    'epoch':joda.Instant.now(),
  })),
  t(Event.REVIEW_ERROR,Stage.RECORDING,Stage.REVIEWING_ERROR,(a,s) => ({
    'message':a.message,
    'selected':s.selected,
  })),
  t(Event.REVIEW,Stage.RECORDING,Stage.REVIEWING,(a,s) => {
    destroyMediaStream(s.stream)
    return {
      'selected':s.selected,
      ...a,
    }
  }),
  t(Event.RECORDING_MEETS,Stage.RECORDING,Stage.RECORDING,(a,s) => ({ ...s,'meets':true })),
  t(Event.UPLOAD,Stage.REVIEWING,Stage.UPLOADING,(a,s) => ({
    'upload':a,
  })),
])

export const VideoInterview : React.FC<Props> = (props) => {
  const [checkDevicePermissions] = useMediaDevices('camera','microphone')
  const [state,dispatch_] = React.useReducer(reducer,{ 'stage':Stage.ERROR,'data':undefined })
  const dispatch = dispatcher(dispatch_)
  console.log('Interview.Render - ',state.stage)

  const setDevices = (devices:MediaDeviceOptions,selected:MediaDevicesSelected | undefined) => {
    dispatch(Event.SET_DEVICES,{
      'devices':devices,
      'selected':selected,
      'wait':(async () => {
        const stream = await navigator.mediaDevices.getUserMedia({
          'video':{
            'facingMode':'user',
            'deviceId':selected?.video?.deviceId,
            'width':screen.orientation.type.startsWith('portrait') ? constVideoHeight : constVideoWidth,
            'height':screen.orientation.type.startsWith('portrait') ? constVideoWidth : constVideoHeight,
          },
          'audio':{
            'deviceId':selected?.audio?.deviceId,
          },
        })
        dispatch(Event.GOTO_ROOT,{ 'devices':devices,'selected':selected,'stream':stream })
      })()
    })
  }
  const dispatchPermissionsCheck = (selected:MediaDevicesSelected | undefined) => {
    dispatch(Event.PERMISSIONS_CHECK,(async () => {
      const result = await checkDevicePermissions()
      switch(result)
      {
        case undefined:
          dispatch(Event.PERMISSIONS_FAILED,{})
          break
        default:
          setDevices(result,selected)
          break
      }
    })())
  }
  React.useEffect(() => {
    dispatchPermissionsCheck(undefined)
  },[])

  const addVideoInterview = async (urlId:string,questionId:string) : Promise<void> =>
    props.replace
    ? (await _postV1(`/video-interviews/replace`,{
      'existing_id':props.replace.id,
      'url_id':urlId,
      'question_id':questionId,
    })).data
    : (await _postV1('/video-interviews/create',{
      'url_id':urlId,
      'question_id':questionId,
    })).data

  return <React.Fragment key={state.stage}>{stateToR<States,React.ReactNode>(state,{
    [Stage.PERMISSIONS]:() => {
      return <>
        <p className="py-2">Verifying access to camera and microphone...</p>
        <div className="d-flex align-items-center justify-content-center">
          <i className="pi pi-video fs-4 mx-3"></i>
          <i className="pi pi-spin pi-spinner-dotted fs-1 mx-3"></i>
          <i className="pi pi-volume-up fs-4 mx-3"></i>
        </div>
      </>
    },
    [Stage.PERMISSIONS_FAILED]:() => <>
      <p>
        Video Interviews require access to your camera and microphone.
      </p>
      <p>
        If you would like to continue, please grant access and click "I've Granted Access".
      </p>
      <p>
        If you have already granted access and are still seeing this message,
        <br/>
        it's possible another application is already using your camera.
      </p>
      <button type="button" className="btn btn-sm btn-primary" onClick={() => dispatchPermissionsCheck(undefined)}>I've Granted Access</button>
    </>,
    [Stage.DEVICES]:(data) => <>
      <MediaDeviceSelection
        audio
        video
        devices={data.devices}
        value={data.selected}
        onChange={(x) => setDevices(data.devices,x)}
        maxWidth={constVideoWidth}
      ></MediaDeviceSelection>
      <div className="position-relative" key="preview">
        <video
          autoPlay
          playsInline
          muted
          width={constVideoWidth+'px'}
          height={constVideoWidth*constVideoAspectRatio+'px'}
          className="rounded-2 z-0 nonsense"
        ></video>
      </div>
      <p>
        When you click <b>Start</b> to begin, a question will be
        displayed and recording will start in <b>{constQuestionTime.isZero() || constQuestionTime.isNegative() ? '.' : `${formatDurationText(constQuestionTime)}`}</b>.
      </p>
      <p>
        Your response must be at least <b>{formatDurationText(constMinLength)}</b>,<br/>
        and recording will end after <br/> <b>{formatDurationText(constMaxLength)}</b>.
      </p>
      <span className="d-flex justify-content-center">
        <button
          type="button"
          className="btn btn-sm btn-success d-flex align-items-center shadow"
          disabled
        >
          <span>START</span>
          <i className="pi pi-video m-1"></i>
        </button>
      </span>
    </>,
    [Stage.ROOT]:(data) => <>
      <MediaDeviceSelection
        audio
        video
        devices={data.devices}
        value={data.selected}
        onChange={(x) => setDevices(data.devices,x)}
        maxWidth={constVideoWidth}
      ></MediaDeviceSelection>
      {data.preview}
      <p>
        When you click <b>Start</b> to begin, a question will be
        displayed and recording will start in <b>{constQuestionTime.isZero() || constQuestionTime.isNegative() ? '.' : `${formatDurationText(constQuestionTime)}`}</b>.
      </p>
      <p>
        Your response must be at least <b>{formatDurationText(constMinLength)}</b>,<br/>
        and recording will end after <br/> <b>{formatDurationText(constMaxLength)}</b>.
      </p>
      <span className="d-flex justify-content-center">
        <button
          type="button"
          className="btn btn-sm btn-success d-flex align-items-center shadow"
          onClick={() => dispatch(Event.QUESTION_LOADING,{ 'question':(async () => {
            const question = await getQuestion(mutableRecentQuestions)
            mutableRecentQuestions = [
              question,
              ...(mutableRecentQuestions.length >= constMaxRecentQuestions ? mutableRecentQuestions.slice(0,constMaxRecentQuestions - 1) : mutableRecentQuestions),
            ]
            dispatch(Event.QUESTION_LOADED,{ 'question':question })
          })() })}
        >
          <span>START</span>
          <i className="pi pi-video m-1"></i>
        </button>
      </span>
    </>,
    [Stage.QUESTION_LOADING]:(data) => <>
      {data.preview}
      <p className="fs-4">Selecting a question...</p>
      <i className="pi pi-question fs-1 mx-3"></i>
    </>,
    [Stage.QUESTION]:(data) => <>
      <TimeEventEmitter
        key="timer-question-1"
        epoch={data.epoch}
        delay={constQuestionTime}
        onEmit={async () => {
          mutableChunks = []
          const recorder = new MediaRecorder(data.stream,recorderSettings())
          recorder.addEventListener('dataavailable',({ data }) => {
            if(data.size > 0)
            {
              mutableChunks.push(data)
            }
          })
          recorder.start()
          dispatch(Event.RECORDING_START,{ 'recorder':recorder })
        }}
      ></TimeEventEmitter>
      {data.preview}
      <div className="z-1 position-absolute top-50 start-50 translate-middle text-white" style={{ fontSize: 80 }}>
        <CountdownCircleTimer
          isPlaying
          duration={constQuestionTime.get(joda.ChronoUnit.SECONDS)}
          colors={['#75FF9A','#75FF9A','#75FF9A','#75FF9A']}
          colorsTime={[7, 5, 2, 0]}
          size={150}
        >
          {({ remainingTime }) => remainingTime}
        </CountdownCircleTimer>
      </div>
      <div className="d-flex justify-content-center">
        <h3 className="w-75">{data.question.question}</h3>
      </div>
      <p className="d-flex align-items-center justify-content-center">
        <button
          type="button"
          className="btn btn-sm d-flex align-items-center shadow text-white"
          style={{ 'background':'#F2925E' }}
          onClick={() => dispatchPermissionsCheck(data.selected)}
        >
          <span className="fw-medium">CANCEL</span>
        </button>
      </p>
    </>,
    [Stage.RECORDING]:(data) => <>
      <TimeEventEmitter
        key="timer-recording-1"
        epoch={data.epoch}
        delay={constMaxLength}
        onEmit={async () => {
          data.recorder.addEventListener('stop',() => {
            const blob = new Blob(mutableChunks,{
              'type':recorderSettings().mimeType,
            })
            dispatch(Event.REVIEW,{
              'blob':blob,
              'objectUrl':URL.createObjectURL(blob),
              'question':data.question,
            })
          })
          data.recorder.stop()
        }}
      ></TimeEventEmitter>
      {data.meets ? undefined :
      <TimeEventEmitter
        key="timer-recording-2"
        epoch={data.epoch}
        delay={constMinLength}
        onEmit={() => dispatch(Event.RECORDING_MEETS,{})}
      ></TimeEventEmitter>}
      {data.preview}
      <div className="d-flex justify-content-center">
        <h3 className="w-75">{data.question.question}</h3>
      </div>
      <KnobCounter start={data.epoch} threshold={15} max={constMaxLength.get(joda.ChronoUnit.SECONDS)}></KnobCounter>
      <p className="d-flex align-items-center justify-content-center">
        <button
          type="button"
          className="btn btn-sm d-flex align-items-center shadow text-white"
          style={{ 'background':data.meets ? '#F2925E' : '#BB2D3B' }}
          onClick={() => {
            switch(data.meets)
            {
              case false:
                data.recorder.addEventListener('stop',() => {
                  dispatch(Event.REVIEW_ERROR,{ 'message':'Recording is too short.' })
                })
                data.recorder.stop()
                break
              case true:
                data.recorder.addEventListener('stop',() => {
                  const blob = new Blob(mutableChunks,{
                    'type':recorderSettings().mimeType,
                  })
                  dispatch(Event.REVIEW,{
                    'blob':blob,
                    'objectUrl':URL.createObjectURL(blob),
                    'question':data.question,
                  })
                })
                data.recorder.stop()
                break
            }
          }}
        >
          <span className="fw-medium">{data.meets ? 'FINISH' : 'CANCEL'}</span>
          <i className="pi pi-stop-circle m-1 rec-button-animation"></i>
        </button>
      </p>
      <p className="text-center">Happy with your response?<br/> Then press <b>Finish</b> after the<br/> question has been answered.</p>
    </>,
    [Stage.REVIEWING]:(data) => <>
      <video
        src={data.objectUrl}
        controls
        className="rounded-2"
        width={constVideoWidth+'px'}
        height={constVideoWidth*constVideoAspectRatio+'px'}
      ></video>
      <div className="d-flex justify-content-center">
        <h3 className="w-75">{data.question.question}</h3>
      </div>
      <p>
        <button type="button" className="btn btn-sm btn-primary me-2" onClick={() => dispatchPermissionsCheck(data.selected)}>Try Again</button>
        <button type="button" className="btn btn-sm btn-primary ms-2" onClick={() => dispatch(Event.UPLOAD,(async () => {
          const presignedUrl = await getUploadUrl()
          await Promise.all([
            uploadFileToPresignedUrl(presignedUrl.url,data.blob),
            addVideoInterview(presignedUrl.id,data.question.id),
          ])
          props.onUpload(presignedUrl.url)
        })())}>Accept and Upload</button>
        {/* <button type="button" className="btn btn-sm btn-primary" onClick={() => doDownload(stateStage.data.objectUrl,'video-interview')}>Download</button> */}
      </p>
    </>,
    [Stage.REVIEWING_ERROR]:(data) => <>
      <p>{data.message}</p>
      <p>
        <button type="button" className="btn btn-sm btn-primary" onClick={() => dispatchPermissionsCheck(data.selected)}>Try Again</button>
      </p>
    </>,
    [Stage.UPLOADING]:(data) => <>
      <Await dependency={data.upload} fallback={<p>Uploading video...</p>}>
        {() => <p>Done.</p>}
      </Await>
    </>,
    [Stage.ERROR]:(data) => <>
      {data}
    </>
  })}</React.Fragment>
}

export default React.memo(VideoInterview)
